// Shared map wrapper for admin fleet/coverage views.
//
// Centralises:
//   - Google Satellite base tiles (matches the look of /admin/fleet-map)
//   - The Leaflet default-marker-icon fix that bundlers break
//   - Smooth wheel zoom (less jumpy than Leaflet's defaults — more wheel
//     pixels per zoom level, fractional zoom snap, debounced wheel events)
//   - Re-measuring when its box changes size: a map that opens in a dialog is
//     made before the dialog has its size, and would draw one corner's tiles
//
// Consumers add their own markers, controllers, and overlay panels as children.

import { useEffect, type ReactNode } from 'react';
import { MapContainer, TileLayer, useMap, type MapContainerProps } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { BASEMAP_URLS } from '../../../utils/constants';
import { FrontDoorTileLayer } from '../../Map/FrontDoorTileLayer';

// One-time default-icon fix. Safe to run multiple times.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/** Tells Leaflet the map's box changed size, so it lays out and loads tiles for all of it. */
function FitToBox() {
  const map = useMap();
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

export interface AdminFleetMapProps extends Omit<MapContainerProps, 'children'> {
  children?: ReactNode;
  /**
   * Override the base tiles. Defaults to Google Satellite to match
   * /admin/fleet-map. Useful if a future view wants OSM dark.
   */
  tileUrl?: string;
  /** When set, tiles go through the tile front door, `tileUrl` behind each one. */
  tileSource?: string;
  tileAttribution?: string;
  tileMaxZoom?: number;
}

export function AdminFleetMap({
  children,
  center = [-30, 170],
  zoom = 4,
  style,
  tileUrl = BASEMAP_URLS.GOOGLE_SATELLITE,
  tileSource,
  tileAttribution = '© Google Maps',
  tileMaxZoom = 20,
  ...rest
}: AdminFleetMapProps) {
  return (
    <MapContainer
      center={center}
      zoom={zoom}
      style={{ height: '100%', width: '100%', ...style }}
      zoomControl
      // ---- Smooth wheel zoom ----
      // Higher wheelPxPerZoomLevel = each notch of mouse wheel moves the
      // zoom less, so fast scrolls don't overshoot. Default is 60.
      wheelPxPerZoomLevel={120}
      // Debounce wheel events so a rapid scroll batch lands as one move.
      wheelDebounceTime={40}
      // Allow fractional zoom levels — kills the "snap to integer" jolt.
      zoomSnap={0.25}
      zoomDelta={0.5}
      {...rest}
    >
      <FitToBox />
      {tileSource
        ? <FrontDoorTileLayer key={tileSource} source={tileSource} fallbackUrl={tileUrl} attribution={tileAttribution} maxZoom={tileMaxZoom} />
        : <TileLayer url={tileUrl} attribution={tileAttribution} maxZoom={tileMaxZoom} />}
      {children}
    </MapContainer>
  );
}

export default AdminFleetMap;
